import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  ArrowRightOnRectangleIcon
} from "@heroicons/react/24/outline";
import { chatAPI, ChatMessage, ChatContext } from "../../services/chatAPI";
import { useAuth } from "../../hooks/useAuth";

interface ChatWidgetProps {
  orderId?: string;
}

const MAX_MESSAGES = 50;
const STORAGE_KEY = "ghdata-chat-history";

const WELCOME_MESSAGE = `Hi! I'm your GHData Market assistant. 👋

I can help you with:
• Order status - just give me your order number
• Data bundles - MTN, AirtelTigo, Telecel
• Wallet - top up, check balance, withdraw
• MTN AFA registration
• WAEC vouchers
• Referrals & becoming an agent

How can I help you today?`;

const QUICK_REPLIES = [
  "Check my order status",
  "How to buy data bundles?",
  "Wallet top-up help",
  "Become an agent",
  "Contact human support"
];

export const ChatWidget: React.FC<ChatWidgetProps> = ({ orderId }) => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed.slice(-MAX_MESSAGES));
          setShowQuickReplies(false);
        }
      }
    } catch (e) {
      // Silently ignore localStorage errors
    }
  }, []);

  useEffect(() => {
    if (!initializedRef.current) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_MESSAGES)));
    } catch (e) {
      // Silently ignore localStorage errors
    }
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const context = useMemo<ChatContext>(() => ({
    orderId,
    language: "en"
  }), [orderId]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMessage: ChatMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setShowQuickReplies(false);
    setIsLoading(true);
    setError(null);

    try {
      const response = await chatAPI.sendMessage(
        [...messages, userMessage],
        context
      );

      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: response.content
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      setError("Failed to send message. Please try again.");
      // Silently ignore send errors
    } finally {
      setIsLoading(false);
    }
  }, [input, messages, context, isLoading]);

  const handleQuickReply = (text: string) => {
    setInput(text);
    sendMessage();
  };

  const clearHistory = () => {
    setMessages([]);
    setShowQuickReplies(true);
    localStorage.removeItem(STORAGE_KEY);
  };

  const escalateToHuman = () => {
    const escalateMsg = "Ill connect you with a human agent. Please hold on...";
    setMessages(prev => [...prev, { role: "assistant", content: escalateMsg }]);
    
    setTimeout(() => {
      window.open("https://wa.me/233500000000?text=Hello%2C%20I%20need%20help%20with%20an%20order", "_blank");
    }, 1000);
  };

  const toggleChat = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) setError(null);
  };

  const renderMessage = (msg: ChatMessage, index: number) => {
    const isUser = msg.role === "user";
    const isFallback = msg.role === "assistant" && msg.content.includes("temporarily unavailable");

    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={clsx(
          "flex gap-2 max-w-[80%]",
          isUser ? "justify-end" : "justify-start"
        )}
      >
        {!isUser && (
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-xs font-bold">
            <SparklesIcon className="w-4 h-4" />
          </div>
        )}

        <div className={clsx(
          "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
          isUser
            ? "bg-primary-600 text-white rounded-br-md"
            : "bg-white dark:bg-navy-800 text-gray-900 dark:text-gray-100 rounded-bl-md shadow-sm border border-gray-100 dark:border-navy-700"
        )}>
          {msg.content}
          {isFallback && (
            <span className="inline-block ml-2 text-xs text-amber-600 dark:text-amber-400">⚠</span>
          )}
        </div>

        {isUser && (
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 dark:bg-navy-700 flex items-center justify-center text-gray-700 dark:text-gray-300 text-xs font-medium">
            U
          </div>
        )}
      </motion.div>
    );
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={toggleChat}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={clsx(
          "fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300",
          isOpen
            ? "bg-red-500 hover:bg-red-600 rotate-45"
            : "bg-gradient-to-br from-primary-500 to-primary-700 hover:from-primary-600 hover:to-primary-800"
        )}
        aria-label={isOpen ? "Close chat" : "Open chat"}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <XMarkIcon className="w-7 h-7 text-white" aria-hidden="true" />
        ) : (
          <>
            <SparklesIcon className="w-7 h-7 text-white" aria-hidden="true" />
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
              1
            </span>
          </>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-50 w-full sm:w-96 max-h-[70vh] bg-white dark:bg-navy-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-navy-700 flex flex-col overflow-hidden"
            role="dialog"
            aria-label="Support chat"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-navy-700 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-navy-800 dark:to-navy-900 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">GHData Assistant</h3>
                  <p className="text-xs text-primary-600 dark:text-primary-400">AI-powered • Usually replies in seconds</p>
                </div>
              </div>
              <button
                onClick={toggleChat}
                className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-navy-700 transition-colors"
                aria-label="Close chat"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ minHeight: 200 }}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <SparklesIcon className="w-12 h-12 text-primary-500/50 dark:text-primary-400/50 mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                    {WELCOME_MESSAGE}
                  </p>
                  {showQuickReplies && (
                    <div className="flex flex-wrap gap-2 justify-center">
                      {QUICK_REPLIES.map((reply, i) => (
                        <motion.button
                          key={reply}
                          onClick={() => handleQuickReply(reply)}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 rounded-full hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                        >
                          {reply}
                        </motion.button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {messages.map(renderMessage)}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Error Banner */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mx-4 mb-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2"
              >
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700 text-xs font-medium"
                >
                  Dismiss
                </button>
              </motion.div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t border-gray-200 dark:border-navy-700 bg-gray-50 dark:bg-navy-800/50 rounded-b-2xl">
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    value={input}
                    onChange={e => {
                      const val = e.target.value;
                      // Limit to 4 rows worth of content
                      if (val.split('\n').length <= 4) {
                        setInput(val);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
                    rows={1}
                    disabled={isLoading}
                    className={clsx(
                      "w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none",
                      isLoading && "opacity-50 cursor-not-allowed"
                    )}
                    aria-label="Chat message"
                    style={{ maxHeight: '4.5rem' }}
                  />
                </div>
                <motion.button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={clsx(
                    "p-2.5 rounded-xl flex-shrink-0 transition-colors",
                    input.trim() && !isLoading
                      ? "bg-primary-600 hover:bg-primary-700 text-white"
                      : "bg-gray-200 dark:bg-navy-700 text-gray-400 cursor-not-allowed"
                  )}
                  aria-label="Send message"
                  aria-disabled={!input.trim() || isLoading}
                >
                  {isLoading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <ChevronDownIcon className="w-5 h-5" />
                    </motion.div>
                  ) : (
                    <PaperAirplaneIcon className="w-5 h-5" />
                  )}
                </motion.button>
              </div>

              {/* Footer actions */}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-navy-700">
                <button
                  onClick={escalateToHuman}
                  className="flex items-center gap-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  Talk to human
                </button>
                <button
                  onClick={clearHistory}
                  className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  Clear chat
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;